<template>
  <div data-testid="qv-direct-banner">
    <!-- Neutral mode line: direct model, confidence + quality tags -->
    <div class="flex flex-wrap items-center justify-center gap-2 text-sm text-slate-500">
      <Tag
        data-testid="qv-direct-badge"
        :value="$t('messages.quickValuation.direct.badge')"
        severity="info"
        class="text-xs!"
      />
      <Tag
        v-if="confidenceLabel"
        :value="confidenceLabel"
        :severity="confidenceSeverity"
        class="text-xs!"
      />
      <Tag
        v-if="qualityLabel"
        :value="qualityLabel"
        severity="secondary"
        class="text-xs!"
      />
    </div>
    <!-- RED-262 Gate v0: the comps-first gate fell back to the direct model — a calm
         notice in place of the info line. Chip stays the RED-290 badge; no second chip.
         Jaan's sentence (16.10) and nothing else: no title, no %, no second line. -->
    <Message
      v-if="isGateBackup"
      data-testid="qv-gate-backup-banner"
      severity="secondary"
      :closable="false"
      class="mt-3 text-left"
    >
      {{ $t('messages.quickValuation.gate.backupReason') }}
    </Message>
    <p v-else class="mt-2 text-xs text-slate-400">
      {{ $t('messages.quickValuation.live.direct.infoLine') }}
    </p>

    <!-- Prominent warning for low-confidence routes (hard_residual & co) -->
    <Message
      v-if="showWarning"
      data-testid="qv-direct-low-confidence"
      severity="warn"
      :closable="false"
      class="mt-3 text-left"
    >
      {{ $t('messages.quickValuation.direct.lowConfidenceWarning') }}
    </Message>
  </div>
</template>

<script setup>
/**
 * RED-356 — LIVE copy (Toomas 02.10 15:40): while `qv-statistical-report` is OFF
 * the Kiirhindamine result page must show exactly what LIVE shows today, so
 * this is origin/main 969c0754's DirectConfidenceBanner.vue unchanged, except that the
 * strings dev rewrote are read from `messages.quickValuation.live.*`, and the Gate v0
 * notice is the same one sentence as the flag-ON banner (RED-262, Jaan 16.10; the server
 * sends the gate only while `qv-gate-v0` is on for the viewer).
 * DirectConfidenceBanner.vue is the flag-ON version. Delete this file with the flag.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  confidence: { type: String, default: null },
  quality: { type: String, default: null },
  lowConfidenceFlag: { type: Boolean, default: false },
  // RED-262 Gate v0 verdict (`prediction.gate`); only `path === 'backup_direct'` renders.
  gate: { type: Object, default: null },
})

const { t, te } = useI18n()

const confidenceLabel = computed(() => {
  const key = `messages.quickValuation.live.direct.confidence.${props.confidence}`
  return props.confidence && te(key) ? t(key) : null
})

const confidenceSeverity = computed(() => ({
  high: 'success',
  medium: 'warn',
  low: 'danger',
}[props.confidence] ?? 'secondary'))

const qualityLabel = computed(() => {
  const key = `messages.quickValuation.live.direct.quality.${props.quality}`
  return props.quality && te(key) ? t(key) : null
})

const showWarning = computed(() => props.lowConfidenceFlag || props.confidence === 'low')

const isGateBackup = computed(() => props.gate?.path === 'backup_direct')
</script>
